import { useCallback, useState } from 'react';
import { Actions, HeartType } from 'src/model/enum/enums';
import { TimeParameter } from 'src/model/enum/parameterEnums';
import { useGeneralSettings } from './useGeneralSettings';
import useProgress from './useProgress';
import useUser from './useUser';

export const useHearts = () => {
    const { userInfo, updateUserInfo, isProUser } = useUser();
    const [isHeartModalOpen, setIsHeartModalOpen] = useState(false);
    const { saveProgress } = useProgress();
    const { generalSettings, updateGeneralSettings } = useGeneralSettings();

    const getHeartCount = useCallback((heartType: HeartType) => {
        switch (heartType) {
            case HeartType.DailyFree:
                return 3;
            case HeartType.Friend:
                return 10;
            case HeartType.Ad:
                return 1;
            case HeartType.Discover:
                return 5;
            default:
                return 0;
        }
    }, []);

    const getHeartText = useCallback((heartType: HeartType) => {
        switch (heartType) {
            case HeartType.DailyFree:
                return `Answer carefully you cannot save up your hearts!\nKeep learning!`;
            case HeartType.Friend:
                return `You have a new friend connected, 10 rewarded for it. `;
            case HeartType.Ad:
                return `You have watched an Ad, a heart rewarded for it. `;
            case HeartType.Discover:
                return `You have discovered 100 words, 5 hearts rewarded for it`;
        }
    }, []);

    const addHeart = useCallback((heartType: HeartType) => {
        if (!userInfo) return;
        updateUserInfo({ Hearts: userInfo.Hearts + getHeartCount(heartType) });
        saveProgress({
            Action: Actions.EarnHeart,
            Value: heartType
        });
    }, [userInfo, updateUserInfo, saveProgress, getHeartCount]);

    const loseHeart = useCallback((count: number, sendProgress: boolean = true) => {
        if (!userInfo) return;
        if (userInfo.Hearts - count < 0) {
            updateUserInfo({ Hearts: 0 });
        } else {
            updateUserInfo({ Hearts: userInfo.Hearts - count });
        }
        if (sendProgress) {
            saveProgress({
                Action: Actions.LoseHeart,
                Value: count.toString()
            });
        }
    }, [userInfo, updateUserInfo, saveProgress]);

    const checkDailyFreeHearts = useCallback(() => {
        if (!userInfo || isProUser()) return false;

        const currentTime = Date.now();
        const lastHeartGetTime = generalSettings?.LastDailyFreeHeartGetDate ? parseInt(generalSettings.LastDailyFreeHeartGetDate) : 0;
        const oneDayInMs = TimeParameter.OneDay;


        if (lastHeartGetTime === 0) {
            updateGeneralSettings({ LastDailyFreeHeartGetDate: currentTime.toString() });
            return false;
        }

        // If first time or 24 hours passed and hearts are below max
        if (currentTime - lastHeartGetTime >= oneDayInMs) {

            updateGeneralSettings({ LastDailyFreeHeartGetDate: currentTime.toString() });

            if (userInfo.Hearts < 3) {
                saveProgress({
                    Action: Actions.LoseHeart,
                    Value: userInfo.Hearts.toString()
                });
                saveProgress({
                    Action: Actions.EarnHeart,
                    Value: HeartType.DailyFree
                });
                updateUserInfo({ Hearts: 3 });
            }
            return true;
        }

        return false;
    }, [userInfo, generalSettings, updateGeneralSettings, isProUser, saveProgress, updateUserInfo]);

    const checkAdHearts = useCallback(() => {
        if (!userInfo || isProUser()) return false;

        const currentTime = Date.now();

        const lastAdHeartGetDate = parseInt(generalSettings.LastAdHeartGetDate);
        const adHeartCount = generalSettings.AdHeartCount;

        // Check if it's a new day
        const oneDayInMs = TimeParameter.OneDay;
        const timeSinceLastAd = currentTime - lastAdHeartGetDate;

        if (timeSinceLastAd >= oneDayInMs) {
            // Reset count for new day
            updateGeneralSettings({
                LastAdHeartGetDate: currentTime.toString(),
                AdHeartCount: 0
            });
            return true;
        }

        // Check if user has watched less than 2 ads today
        if (adHeartCount < 2) {
            updateGeneralSettings({
                AdHeartCount: adHeartCount + 1
            });
            return true;
        }

        return false;
    }, [userInfo, generalSettings, updateGeneralSettings, isProUser]);

    const checkUncoveredWordsHearts = useCallback(() => {
        if (!userInfo || isProUser()) return false;

        const currentCount = generalSettings.UncoverWordCount;
        if (currentCount >= 100) {
            // Award one heart
            addHeart(HeartType.Discover);
            updateGeneralSettings({ UncoverWordCount: currentCount - 100 });
            return true;
        }
        return false;
    }, [updateGeneralSettings, generalSettings, addHeart, userInfo, isProUser]);

    return {
        checkDailyFreeHearts,
        checkAdHearts,
        isHeartModalOpen,
        closeHeartModal: () => setIsHeartModalOpen(false),
        addHeart,
        loseHeart,
        getHeartCount,
        getHeartText,
        checkUncoveredWordsHearts,

    };
}; 